import { useEffect, useRef } from 'react';

const TRIGGER = 70; // how far (px) to pull before letting go refreshes
const MAX_PULL = 120;
const MIN_SPIN_MS = 700; // spin at least this long so the refresh reads as one
const SETTLE_MS = 600; // frog slides back up and the feed slides in
const FROG = '.new-event-button';

// Skip pulls that start in a pop-up or text box, so dialogs and typing scroll normally.
function startsInIgnoredArea(target) {
  return Boolean(
    target &&
      target.closest &&
      target.closest('[role="dialog"], input, textarea, select, [contenteditable="true"], .bell-panel')
  );
}

// Phone pull-to-refresh on the home page: the frog button itself is pulled
// down and turns with the finger, glows once far enough, then spins while
// onRefresh reloads the feed in place (no page reload, so no flash). When it's
// done the frog finishes its turn and slides back up as the feed slides in.
// The browser's own spinner is switched off in index.css while the frog
// button is on the page. Styling hooks on <html>: --ptr-pull, --ptr-turn,
// .ptr-pulling, .ptr-ready, .ptr-refreshing, .ptr-done.
export default function PullToRefresh({ onRefresh }) {
  const onRefreshRef = useRef(onRefresh);
  onRefreshRef.current = onRefresh;

  useEffect(() => {
    const root = document.documentElement;
    let startY = null;
    let pull = 0;
    let refreshing = false;

    const setPull = (value) => {
      pull = value;
      root.style.setProperty('--ptr-pull', `${value}px`);
      root.style.setProperty('--ptr-turn', `${value * 3}deg`);
      root.classList.toggle('ptr-ready', value >= TRIGGER);
    };

    let doneTimer = null;

    const reset = () => {
      root.classList.remove('ptr-pulling', 'ptr-ready');
      root.style.removeProperty('--ptr-pull');
      root.style.removeProperty('--ptr-turn');
      pull = 0;
    };

    const onStart = (event) => {
      startY = null;
      if (
        refreshing ||
        event.touches.length !== 1 ||
        window.scrollY > 0 ||
        !document.querySelector(FROG) ||
        startsInIgnoredArea(event.target)
      ) return;
      startY = event.touches[0].clientY;
    };

    const onMove = (event) => {
      if (startY === null) return;
      const dy = event.touches[0].clientY - startY;
      if (dy <= 0 || window.scrollY > 0) {
        if (pull) reset();
        return;
      }
      root.classList.add('ptr-pulling');
      // Resistance: the frog moves slower than the finger.
      setPull(Math.min(MAX_PULL, dy * 0.5));
      if (event.cancelable) event.preventDefault();
    };

    // Stop spinning at the end of a full turn (no jump), then slide back up.
    const settle = () => {
      const frog = document.querySelector(FROG);
      let finished = false;
      const finish = () => {
        if (finished) return;
        finished = true;
        if (frog) frog.removeEventListener('animationiteration', finish);
        root.classList.remove('ptr-refreshing');
        reset();
        root.classList.add('ptr-done');
        doneTimer = window.setTimeout(() => {
          root.classList.remove('ptr-done');
          refreshing = false;
        }, SETTLE_MS);
      };
      if (frog) frog.addEventListener('animationiteration', finish);
      window.setTimeout(finish, 900); // in case the animation event never comes
    };

    const refresh = async () => {
      const load = onRefreshRef.current;
      if (!load) {
        window.location.reload();
        return;
      }
      const minimumSpin = new Promise((resolve) => window.setTimeout(resolve, MIN_SPIN_MS));
      try {
        await Promise.all([load(), minimumSpin]);
      } catch (error) {
        // Keep the old feed; the frog still settles back.
      }
      settle();
    };

    const onEnd = () => {
      if (startY === null) return;
      startY = null;
      root.classList.remove('ptr-pulling');
      if (pull >= TRIGGER) {
        refreshing = true;
        setPull(TRIGGER);
        root.classList.add('ptr-refreshing');
        refresh();
      } else {
        reset();
      }
    };

    window.addEventListener('touchstart', onStart, { passive: true });
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onEnd);
    window.addEventListener('touchcancel', onEnd);
    return () => {
      window.removeEventListener('touchstart', onStart);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onEnd);
      window.removeEventListener('touchcancel', onEnd);
      window.clearTimeout(doneTimer);
      reset();
      root.classList.remove('ptr-refreshing', 'ptr-done');
    };
  }, []);

  return null;
}
